// The four terrain LOOKS, as GLSL snippets and uniform lists (R828,
// R3276, ui-map-interface.md §4.4, themes.md §9).
//
// A World theme carries one `terrain` value, and each value replaces the
// whole picture of the ridges and the trenches. `default` is the
// Cartographic pair the client has always drawn — Contour Massif over
// Hachure Cut (R100). `form` is Scarp over Depth Ramp, and it states
// HEIGHT. `material` is Boulder Field over Stratified Wall, and it states
// what the ground is made of. `chart` is a flat ridge over a sounding hole,
// and it reads rock as a danger (R3276).
//
// **Every look is written twice.** A part carries its GLSL and its WGSL
// twin side by side (`helpers`/`helpersWgsl`, `paint`/`paintWgsl`), because
// the WebGPU backend of R847 draws the same three pictures from WGSL. One
// table holds both, so a look that gains a stroke gains it in both
// languages or fails `terrain-styles.test.ts` at once. The declarations
// have no twin: WGSL generates the whole uniform block from
// `terrainStyleUniforms(id)`.
//
// **One program per value.** This file holds the pieces; `terrain.ts`
// assembles them the way `territory.ts` assembles its variants, and
// `layers/terrain.ts` compiles a value's program on the first frame that
// draws it. The shipping look therefore pays for nothing: `default` builds
// exactly the bytes the client compiled before this file existed.
//
// **The translated-copy rule.** Three of the four treatments draw a copy of
// a body moved by an offset `o`. The client has the body only as a signed
// distance `s`, positive inside, and its unit inward normal `n`. To first
// order
//
//   s(p − o) ≈ s(p) + dot(o, −n)
//
// and the step is EXACT on a straight edge, because the normal does not
// turn there. So a lit crest, a cast shadow and a flank band each cost one
// dot product and no second field. The limit is a concave notch, where the
// normal turns inside one stroke width and the copy pinches; §4.4 states it.
//
// **No screen-space derivative.** `prelude.ts` bans `fwidth` and its two
// relatives, and `shaders.test.ts` holds every source in this directory to
// that. The chunk of Boulder Field is therefore a hexagon whose face normal
// is read off the SDF itself, and never off the pixel next door.

import { rgb } from '../../render/color';
import type { UniformSpec } from './prelude';
import {
  TERRAIN_RIDGE_FILL,
  TERRAIN_RIDGE_RIM,
  TERRAIN_RINGS,
  TERRAIN_RING_STEP,
  TERRAIN_RING_WIDTH,
  TERRAIN_STYLE_FIGURES,
  TERRAIN_TICK_MAX,
  TERRAIN_TICK_MIN,
  TERRAIN_TICK_ROOT,
  TERRAIN_TICK_ROOT_STYLE,
  TERRAIN_TICK_TIP_STYLE,
  TERRAIN_TRENCH_FLOOR,
  TERRAIN_TRENCH_RIM,
  TERRAIN_VOID_COLOR,
  TERRAIN_VOID_CORE_INSET,
  TERRAIN_TICK_STANDOFF,
  TERRAIN_TICK_THREE_CHANCE,
  TERRAIN_VOID_FEATHER_ALPHA,
  TERRAIN_VOID_FEATHER_COUNT,
  TERRAIN_VOID_FEATHER_FROM,
  TERRAIN_VOID_FEATHER_STEP,
} from '../../gen/presentation';
import { GLMOD_WGSL } from './wgsl/common';
import type { TerrainStyle } from '../../ui/settings';

/**
 * The rim ticks of the `default` look, which alone draws a tick
 * (`[presentation.terrain] tick_three_chance` and `tick_standoff_px`): the
 * chance a rim edge carries three ticks rather than two, and how far outside
 * the hex edge a tick starts, in world px.
 */
export { TERRAIN_TICK_STANDOFF, TERRAIN_TICK_THREE_CHANCE };

/**
 * One half of a look: its declarations, its helpers, its paint and its
 * uniforms.
 *
 * Each of the two source fields carries a WGSL twin beside it (the WebGPU
 * plan of 2026-09-11, group C). The twin is a FIELD and not a second table,
 * because the two halves must be read side by side: a look added with no
 * twin then shows as a missing field, and `terrain-styles.test.ts` holds
 * every look to the pair.
 *
 * `decl` has no twin. WGSL declares no uniform of its own: the whole block
 * is generated from `terrainStyleUniforms(id)` in front of the body.
 */
export interface TerrainStylePart {
  /** The uniform declarations the half adds to the source. */
  readonly decl: string;
  /** The functions it wants in front of `terrainPaint`. */
  readonly helpers: string;
  /** The WGSL twin of `helpers`. */
  readonly helpersWgsl: string;
  /** The statements it contributes to `terrainPaint`, in draw order. */
  readonly paint: string;
  /** The WGSL twin of `paint`. */
  readonly paintWgsl: string;
  /** Its uniforms, whose defaults are the data layer's figures. */
  readonly uniforms: readonly UniformSpec[];
}

/** One terrain look: what it draws on a ridge, and what it draws in a trench. */
export interface TerrainStyleDef {
  readonly ridge: TerrainStylePart;
  readonly trench: TerrainStylePart & {
    /** Does the look hang hachure ticks off the rim? Only `default` does. */
    readonly hachures: boolean;
  };
}

const F = TERRAIN_STYLE_FIGURES;

// ---------------------------------------------------------------------------
// `default` — Contour Massif over Hachure Cut (R100)
// ---------------------------------------------------------------------------

const DEFAULT_RIDGE: TerrainStylePart = {
  decl: `
uniform vec3  uRidgeRgb;      uniform float uRidgeA;
uniform vec3  uRidgeRimRgb;   uniform float uRidgeRimA;   uniform float uRidgeRimW;

uniform vec3  uRingRgb[4];
uniform float uRingA[4];
uniform float uRingStep;
uniform float uRingWidth;
`,
  helpers: '',
  helpersWgsl: '',
  paint: `
  // 5 — the ridge body. Rock sits on top of water, so it covers the ticks.
  outc = over(outc, uRidgeRgb, uRidgeA * covR);

  // 6 — the four contour rings, lightening inward, clipped to the body.
  if (kind0 == 2 && compR > 0.0) {
    float dEdt = distAt(distUv(p)).g;
    float dIn = mix(max(sR, 0.0), dEdt, smoothstep(uEdtBlend.x, uEdtBlend.y, dEdt));
    float u = dIn / compR;
    for (int r = 0; r < 4; r++) {
      float dr = abs(u - uRingStep * float(r + 1)) * compR;
      outc = over(outc, uRingRgb[r],
                  uRingA[r] * lineCov(dr, 0.5 * uRingWidth, px) * covR);
    }
  }

  // 7 — the ridge perimeter, over the top of the rings.
  outc = over(outc, uRidgeRimRgb,
              uRidgeRimA * lineCov(abs(sR), 0.5 * uRidgeRimW, px));
`,
  paintWgsl: `
  // 5 — the ridge body. Rock sits on top of water, so it covers the ticks.
  outc = over(outc, U.uRidgeRgb, U.uRidgeA * covR);

  // 6 — the four contour rings, lightening inward, clipped to the body.
  // The ring table is an array of the block, and the four alphas are the
  // four components of one vec4f, which is how a float[4] rides the
  // uniform address space (wgsl/layout.ts).
  if (kind0 == 2 && compR > 0.0) {
    let dEdt = distAt(distUv(p)).g;
    let dIn = mix(max(sR, 0.0), dEdt, smoothstep(U.uEdtBlend.x, U.uEdtBlend.y, dEdt));
    let u = dIn / compR;
    for (var r: i32 = 0; r < 4; r++) {
      let dr = abs(u - U.uRingStep * f32(r + 1)) * compR;
      outc = over(outc, U.uRingRgb[r],
                  U.uRingA[r] * lineCov(dr, 0.5 * U.uRingWidth, px) * covR);
    }
  }

  // 7 — the ridge perimeter, over the top of the rings.
  outc = over(outc, U.uRidgeRimRgb,
              U.uRidgeRimA * lineCov(abs(sR), 0.5 * U.uRidgeRimW, px));
`,
  uniforms: [
    { name: 'uRidgeRgb', type: 'vec3', value: rgb(TERRAIN_RIDGE_FILL.color) },
    { name: 'uRidgeA', type: 'float', value: TERRAIN_RIDGE_FILL.alpha },
    { name: 'uRidgeRimRgb', type: 'vec3', value: rgb(TERRAIN_RIDGE_RIM.color) },
    { name: 'uRidgeRimA', type: 'float', value: TERRAIN_RIDGE_RIM.alpha },
    { name: 'uRidgeRimW', type: 'float', value: TERRAIN_RIDGE_RIM.width },
    { name: 'uRingRgb', type: 'vec3[]', value: TERRAIN_RINGS.flatMap((r) => rgb(r.color)), note: '4 entries' },
    { name: 'uRingA', type: 'float[]', value: TERRAIN_RINGS.map((r) => r.alpha), note: '4 entries' },
    { name: 'uRingStep', type: 'float', value: TERRAIN_RING_STEP },
    { name: 'uRingWidth', type: 'float', value: TERRAIN_RING_WIDTH },
  ],
};

const DEFAULT_TRENCH: TerrainStylePart & { hachures: boolean } = {
  hachures: true,
  decl: `
uniform vec3  uFloorRgb;      uniform float uFloorA;
uniform vec3  uTrenchRimRgb;  uniform float uTrenchRimA;  uniform float uTrenchRimW;

uniform vec3  uVoidRgb;
uniform float uVoidFrom;
uniform float uVoidStep;
uniform int   uVoidCount;
uniform float uVoidFeatherA;
uniform float uVoidCoreInset;

uniform int   uHachureMode;
uniform float uTickScale;
uniform float uTickMin;
uniform float uTickMax;
uniform float uTickRoot;
uniform float uTickThree;
uniform float uTickStandoff;
uniform vec3  uTickRootRgb;   uniform float uTickRootA;   uniform float uTickRootW;
uniform vec3  uTickTipRgb;    uniform float uTickTipA;    uniform float uTickTipW;
`,
  helpers: `
uint cellKey(ivec2 c) { return uint((c.y + 4) * (uGrid.x + 8) + (c.x + 4)); }
// The key of an unordered cell pair: the two cells of a rim edge must hash
// the same whichever one the pixel belongs to.
uint edgeKey(ivec2 a, ivec2 b) {
  uint ka = cellKey(a), kb = cellKey(b);
  uint lo = min(ka, kb), hi = max(ka, kb);
  return hashU(lo * 0x9e3779b9u + hi + uint(uHachureSeed));
}

// Coverage of an axis-aligned box in the frame of a segment: the segment
// runs from A along the unit vector u for length len, half-width hw.
float segCov(vec2 p, vec2 a, vec2 u, float len, float hw, float px) {
  vec2 v = p - a;
  float t = dot(v, u);
  float n = dot(v, vec2(-u.y, u.x));
  return lineCov(abs(n), hw, px) * lineCov(abs(t - 0.5 * len), 0.5 * len, px);
}

// The ticks hanging off ONE rim edge: the edge of trench cell t in
// direction k, whose neighbour is open water. The edge lies between corners
// k and k+1 of t (world.ts:116). Each tick starts uTickStandoff px outside
// the edge and runs toward the cell centre, so it spans the rim wherever
// the smoothed outline bulges past the hex edge.
vec4 ticksOnEdge(vec4 dst, vec2 p, ivec2 t, int k, ivec2 o, float px, float cov) {
  vec2 ctr = cellCenter(t);
  vec2 c0 = ctr + hexCorner(k);
  vec2 c1 = ctr + hexCorner((k + 1) % 6);
  vec2 mid = 0.5 * (c0 + c1);
  vec2 dir = normalize(ctr - mid);            // the edge's inward normal

  // The whole edge is rejected before the hash and the two segment fields.
  // Every tick of this edge lives in one box: from uTickStandoff outside the
  // edge to uTickMax inward, and no further along the edge than its own
  // ends. A pixel outside that box cannot be on any of them, and a rim cell
  // is far larger than the box (R431, the GPU work).
  vec2 v = p - mid;
  float tn = dot(v, dir);
  float tt = dot(v, vec2(-dir.y, dir.x));
  float pad = 0.5 * max(uTickRootW, uTickTipW) + px;
  if (tn < -uTickStandoff - pad) return dst;
  if (tn > uTickMax * uTickScale - uTickStandoff + pad) return dst;
  if (abs(tt) > 0.5 * length(c1 - c0) + pad) return dst;

  uint h = edgeKey(t, o);
  int n = (hash01(h) < uTickThree) ? 3 : 2;

  for (int j = 0; j < 3; j++) {
    if (j >= n) break;
    uint hj = hashU(h + uint(j) * 0x85ebca6bu);
    vec2 base = mix(c0, c1, (float(j) + 0.5) / float(n));
    vec2 start = base - dir * uTickStandoff;
    float len = (uTickMin + (uTickMax - uTickMin) * hash01(hj)) * uTickScale;
    float rootLen = len * uTickRoot;
    // The tick is clipped by the body's own coverage, so it hangs INWARD
    // off the smoothed rim however far outside the hex edge that rim runs.
    dst = over(dst, uTickRootRgb,
               uTickRootA * cov * segCov(p, start, dir, rootLen, 0.5 * uTickRootW, px));
    dst = over(dst, uTickTipRgb,
               uTickTipA * cov * segCov(p, start + dir * rootLen, dir, len - rootLen,
                                        0.5 * uTickTipW, px));
  }
  return dst;
}
`,
  helpersWgsl: `
fn cellKey(c: vec2i) -> u32 {
  return u32((c.y + 4) * (U.uGrid.x + 8) + (c.x + 4));
}
// The key of an unordered cell pair: the two cells of a rim edge must hash
// the same whichever one the pixel belongs to.
fn edgeKey(a: vec2i, b: vec2i) -> u32 {
  let ka = cellKey(a);
  let kb = cellKey(b);
  let lo = min(ka, kb);
  let hi = max(ka, kb);
  return hashU(lo * 0x9e3779b9u + hi + u32(U.uHachureSeed));
}

// Coverage of an axis-aligned box in the frame of a segment: the segment
// runs from A along the unit vector u for length len, half-width hw.
fn segCov(p: vec2f, a: vec2f, u: vec2f, len: f32, hw: f32, px: f32) -> f32 {
  let v = p - a;
  let t = dot(v, u);
  let n = dot(v, vec2f(-u.y, u.x));
  return lineCov(abs(n), hw, px) * lineCov(abs(t - 0.5 * len), 0.5 * len, px);
}

// The ticks hanging off ONE rim edge: the edge of trench cell t in
// direction k, whose neighbour is open water. The edge lies between corners
// k and k+1 of t (world.ts:116). Each tick starts uTickStandoff px outside
// the edge and runs toward the cell centre, so it spans the rim wherever
// the smoothed outline bulges past the hex edge.
//
// GLSL assigns to its own \`dst\` parameter. A WGSL parameter is immutable,
// so the twin copies it into a var of the same name and returns that.
fn ticksOnEdge(dstIn: vec4f, p: vec2f, t: vec2i, k: i32, o: vec2i, px: f32, cov: f32) -> vec4f {
  var dst = dstIn;
  let ctr = cellCenter(t);
  let c0 = ctr + hexCorner(k);
  let c1 = ctr + hexCorner((k + 1) % 6);
  let mid = 0.5 * (c0 + c1);
  let dir = normalize(ctr - mid);             // the edge's inward normal

  // The whole edge is rejected before the hash and the two segment fields.
  // Every tick of this edge lives in one box: from uTickStandoff outside the
  // edge to uTickMax inward, and no further along the edge than its own
  // ends. A pixel outside that box cannot be on any of them, and a rim cell
  // is far larger than the box (R431, the GPU work).
  let v = p - mid;
  let tn = dot(v, dir);
  let tt = dot(v, vec2f(-dir.y, dir.x));
  let pad = 0.5 * max(U.uTickRootW, U.uTickTipW) + px;
  if (tn < -U.uTickStandoff - pad) { return dst; }
  if (tn > U.uTickMax * U.uTickScale - U.uTickStandoff + pad) { return dst; }
  if (abs(tt) > 0.5 * length(c1 - c0) + pad) { return dst; }

  let h = edgeKey(t, o);
  var n = 2;
  if (hash01(h) < U.uTickThree) { n = 3; }

  for (var j: i32 = 0; j < 3; j++) {
    if (j >= n) { break; }
    let hj = hashU(h + u32(j) * 0x85ebca6bu);
    let base = mix(c0, c1, (f32(j) + 0.5) / f32(n));
    let start = base - dir * U.uTickStandoff;
    let len = (U.uTickMin + (U.uTickMax - U.uTickMin) * hash01(hj)) * U.uTickScale;
    let rootLen = len * U.uTickRoot;
    // The tick is clipped by the body's own coverage, so it hangs INWARD
    // off the smoothed rim however far outside the hex edge that rim runs.
    dst = over(dst, U.uTickRootRgb,
               U.uTickRootA * cov * segCov(p, start, dir, rootLen, 0.5 * U.uTickRootW, px));
    dst = over(dst, U.uTickTipRgb,
               U.uTickTipA * cov * segCov(p, start + dir * rootLen, dir, len - rootLen,
                                          0.5 * U.uTickTipW, px));
  }
  return dst;
}
`,
  paint: `
  // 1 — the trench floor, holes cut for free by the field.
  outc = over(outc, uFloorRgb, uFloorA * covT);

  // 2 — the void: seven feathers then a hard core. compR <= 0 means the
  // component is under TERRAIN_VOID_MIN_CELLS and takes no core at all.
  // The four-tap read is paid HERE and in the rings, and nowhere else: it
  // is the iso-line stack that needs a smooth field, not the rim.
  if (kind0 == 1 && compR > 0.0) {
    float dEdt = distAt(distUv(p)).r;
    float dIn = mix(max(sT, 0.0), dEdt, smoothstep(uEdtBlend.x, uEdtBlend.y, dEdt));
    float u = dIn / compR;
    float dupx = px / compR;
    float n = 0.0;
    for (int i = 0; i < 8; i++) {
      if (i >= uVoidCount) break;
      // The presentation figures are INSETS from the outline; u is depth.
      float ui = 1.0 - (uVoidFrom - uVoidStep * float(i));
      n += clamp((u - ui) / dupx + 0.5, 0.0, 1.0);
    }
    float aF = 1.0 - pow(1.0 - uVoidFeatherA, n);
    float aC = clamp((u - (1.0 - uVoidCoreInset)) / dupx + 0.5, 0.0, 1.0);
    outc = over(outc, uVoidRgb, (aF + aC * (1.0 - aF)) * covT);
  }

  // 3 — the hachures. A trench pixel looks at its own rim edges; an open
  // pixel looks at the edge each trench neighbour turns toward it, because
  // a tick starts outside the hex edge.
  if (uHachureMode == 1) {
    if (kind0 == 1) {
      for (int k = 0; k < 6; k++) {
        ivec2 n1 = neighbor(c0, k);
        if (kindOf(n1) != 1) outc = ticksOnEdge(outc, p, c0, k, n1, px, covT);
      }
    } else if (kind0 == 0) {
      for (int k = 0; k < 6; k++) {
        ivec2 n1 = neighbor(c0, k);
        if (kindOf(n1) == 1) outc = ticksOnEdge(outc, p, n1, (k + 3) % 6, c0, px, covT);
      }
    }
  }

  // 4 — the trench rim, round every loop including the pockets.
  outc = over(outc, uTrenchRimRgb,
              uTrenchRimA * lineCov(abs(sT), 0.5 * uTrenchRimW, px));
`,
  paintWgsl: `
  // 1 — the trench floor, holes cut for free by the field.
  outc = over(outc, U.uFloorRgb, U.uFloorA * covT);

  // 2 — the void: seven feathers then a hard core. compR <= 0 means the
  // component is under TERRAIN_VOID_MIN_CELLS and takes no core at all.
  // The four-tap read is paid HERE and in the rings, and nowhere else: it
  // is the iso-line stack that needs a smooth field, not the rim.
  if (kind0 == 1 && compR > 0.0) {
    let dEdt = distAt(distUv(p)).r;
    let dIn = mix(max(sT, 0.0), dEdt, smoothstep(U.uEdtBlend.x, U.uEdtBlend.y, dEdt));
    let u = dIn / compR;
    let dupx = px / compR;
    var n = 0.0;
    for (var i: i32 = 0; i < 8; i++) {
      if (i >= U.uVoidCount) { break; }
      // The presentation figures are INSETS from the outline; u is depth.
      let ui = 1.0 - (U.uVoidFrom - U.uVoidStep * f32(i));
      n += clamp((u - ui) / dupx + 0.5, 0.0, 1.0);
    }
    let aF = 1.0 - pow(1.0 - U.uVoidFeatherA, n);
    let aC = clamp((u - (1.0 - U.uVoidCoreInset)) / dupx + 0.5, 0.0, 1.0);
    outc = over(outc, U.uVoidRgb, (aF + aC * (1.0 - aF)) * covT);
  }

  // 3 — the hachures. A trench pixel looks at its own rim edges; an open
  // pixel looks at the edge each trench neighbour turns toward it, because
  // a tick starts outside the hex edge.
  if (U.uHachureMode == 1) {
    if (kind0 == 1) {
      for (var k: i32 = 0; k < 6; k++) {
        let n1 = neighbor(c0, k);
        if (kindOf(n1) != 1) { outc = ticksOnEdge(outc, p, c0, k, n1, px, covT); }
      }
    } else if (kind0 == 0) {
      for (var k: i32 = 0; k < 6; k++) {
        let n1 = neighbor(c0, k);
        if (kindOf(n1) == 1) { outc = ticksOnEdge(outc, p, n1, (k + 3) % 6, c0, px, covT); }
      }
    }
  }

  // 4 — the trench rim, round every loop including the pockets.
  outc = over(outc, U.uTrenchRimRgb,
              U.uTrenchRimA * lineCov(abs(sT), 0.5 * U.uTrenchRimW, px));
`,
  uniforms: [
    { name: 'uFloorRgb', type: 'vec3', value: rgb(TERRAIN_TRENCH_FLOOR.color) },
    { name: 'uFloorA', type: 'float', value: TERRAIN_TRENCH_FLOOR.alpha },
    { name: 'uTrenchRimRgb', type: 'vec3', value: rgb(TERRAIN_TRENCH_RIM.color) },
    { name: 'uTrenchRimA', type: 'float', value: TERRAIN_TRENCH_RIM.alpha },
    { name: 'uTrenchRimW', type: 'float', value: TERRAIN_TRENCH_RIM.width },
    { name: 'uVoidRgb', type: 'vec3', value: rgb(TERRAIN_VOID_COLOR) },
    { name: 'uVoidFrom', type: 'float', value: TERRAIN_VOID_FEATHER_FROM },
    { name: 'uVoidStep', type: 'float', value: TERRAIN_VOID_FEATHER_STEP },
    { name: 'uVoidCount', type: 'int', value: TERRAIN_VOID_FEATHER_COUNT },
    { name: 'uVoidFeatherA', type: 'float', value: TERRAIN_VOID_FEATHER_ALPHA },
    { name: 'uVoidCoreInset', type: 'float', value: TERRAIN_VOID_CORE_INSET },
    { name: 'uHachureMode', type: 'int', value: 1 },
    { name: 'uTickScale', type: 'float', value: 1 },
    { name: 'uTickMin', type: 'float', value: TERRAIN_TICK_MIN },
    { name: 'uTickMax', type: 'float', value: TERRAIN_TICK_MAX },
    { name: 'uTickRoot', type: 'float', value: TERRAIN_TICK_ROOT },
    { name: 'uTickThree', type: 'float', value: TERRAIN_TICK_THREE_CHANCE },
    { name: 'uTickStandoff', type: 'float', value: TERRAIN_TICK_STANDOFF },
    { name: 'uTickRootRgb', type: 'vec3', value: rgb(TERRAIN_TICK_ROOT_STYLE.color) },
    { name: 'uTickRootA', type: 'float', value: TERRAIN_TICK_ROOT_STYLE.alpha },
    { name: 'uTickRootW', type: 'float', value: TERRAIN_TICK_ROOT_STYLE.width },
    { name: 'uTickTipRgb', type: 'vec3', value: rgb(TERRAIN_TICK_TIP_STYLE.color) },
    { name: 'uTickTipA', type: 'float', value: TERRAIN_TICK_TIP_STYLE.alpha },
    { name: 'uTickTipW', type: 'float', value: TERRAIN_TICK_TIP_STYLE.width },
  ],
};

// ---------------------------------------------------------------------------
// `form` — Scarp over Depth Ramp (card 001/Form)
// ---------------------------------------------------------------------------

const SCARP = F.form.ridge;

const FORM_RIDGE: TerrainStylePart = {
  decl: `
uniform vec3  uRidgeRgb;      uniform float uRidgeA;
uniform vec3  uRidgeRimRgb;   uniform float uRidgeRimA;   uniform float uRidgeRimW;

uniform vec3  uRidgeShadowRgb; uniform float uRidgeShadowA; uniform vec2 uRidgeShadowOff;
uniform vec2  uScarpFlankOff;
uniform float uScarpFlankDrop;
uniform vec3  uScarpFlankRgb[7];
uniform vec3  uScarpCrestRgb; uniform float uScarpCrestA; uniform float uScarpCrestW;
uniform float uScarpCrestLift;
uniform vec3  uScarpHairRgb;  uniform float uScarpHairA;  uniform float uScarpHairW;
uniform float uScarpHairLift;
`,
  helpers: '',
  helpersWgsl: '',
  paint: `
  // 5 — SCARP. The cast shadow first: the whole mass, copied by the
  // offset. The copy is the translated-copy rule of terrain-styles.ts,
  // so it costs one dot product and no second field.
  outc = over(outc, uRidgeShadowRgb,
              uRidgeShadowA * fillCov(sR - dot(uRidgeShadowOff, nR), px));

  // The flank: the mass copied SEVEN times along uScarpFlankOff, nearest
  // copy first, each one fainter than the one before it. Copy k covers a
  // pixel when sR - k*dot(o, nR) > 0, so the nearest copy that covers is
  // the ceiling of that ratio and the deeper ones are behind it. g <= 0
  // is the lit side of the mass, where no copy ever shows.
  if (sR < 0.0) {
    float g = -dot(uScarpFlankOff, nR);
    if (g > 0.0) {
      int k = int(ceil(-sR / g));
      if (k >= 1 && k <= 7) {
        outc = over(outc, uScarpFlankRgb[k - 1],
                    (1.0 - uScarpFlankDrop * float(k) / 7.0)
                      * fillCov(sR + float(k) * g, px));
      }
    }
  }

  // The crest plate, then the dark perimeter under the two lit strokes.
  outc = over(outc, uRidgeRgb, uRidgeA * covR);
  outc = over(outc, uRidgeRimRgb,
              uRidgeRimA * lineCov(abs(sR), 0.5 * uRidgeRimW, px));

  // The lit crest and its hairline: the SAME perimeter, copied toward the
  // light by the lift. On the far side of the mass the copy falls inside
  // the rock, which is where the reference draws it too.
  outc = over(outc, uScarpCrestRgb,
              uScarpCrestA * lineCov(abs(sR + uScarpCrestLift * nR.y),
                                     0.5 * uScarpCrestW, px));
  outc = over(outc, uScarpHairRgb,
              uScarpHairA * lineCov(abs(sR + uScarpHairLift * nR.y),
                                    0.5 * uScarpHairW, px));
`,
  paintWgsl: `
  // 5 — SCARP. The cast shadow first: the whole mass, copied by the
  // offset. The copy is the translated-copy rule of terrain-styles.ts,
  // so it costs one dot product and no second field.
  outc = over(outc, U.uRidgeShadowRgb,
              U.uRidgeShadowA * fillCov(sR - dot(U.uRidgeShadowOff, nR), px));

  // The flank: the mass copied SEVEN times along uScarpFlankOff, nearest
  // copy first, each one fainter than the one before it. Copy k covers a
  // pixel when sR - k*dot(o, nR) > 0, so the nearest copy that covers is
  // the ceiling of that ratio and the deeper ones are behind it. g <= 0
  // is the lit side of the mass, where no copy ever shows.
  //
  // The table read stays INSIDE the k test. WGSL evaluates both arms of a
  // select, and a constant index out of range in the dead arm is a compile
  // error, so the gate must be an if.
  if (sR < 0.0) {
    let g = -dot(U.uScarpFlankOff, nR);
    if (g > 0.0) {
      let k = i32(ceil(-sR / g));
      if (k >= 1 && k <= 7) {
        outc = over(outc, U.uScarpFlankRgb[k - 1],
                    (1.0 - U.uScarpFlankDrop * f32(k) / 7.0)
                      * fillCov(sR + f32(k) * g, px));
      }
    }
  }

  // The crest plate, then the dark perimeter under the two lit strokes.
  outc = over(outc, U.uRidgeRgb, U.uRidgeA * covR);
  outc = over(outc, U.uRidgeRimRgb,
              U.uRidgeRimA * lineCov(abs(sR), 0.5 * U.uRidgeRimW, px));

  // The lit crest and its hairline: the SAME perimeter, copied toward the
  // light by the lift. On the far side of the mass the copy falls inside
  // the rock, which is where the reference draws it too.
  outc = over(outc, U.uScarpCrestRgb,
              U.uScarpCrestA * lineCov(abs(sR + U.uScarpCrestLift * nR.y),
                                       0.5 * U.uScarpCrestW, px));
  outc = over(outc, U.uScarpHairRgb,
              U.uScarpHairA * lineCov(abs(sR + U.uScarpHairLift * nR.y),
                                      0.5 * U.uScarpHairW, px));
`,
  uniforms: [
    { name: 'uRidgeRgb', type: 'vec3', value: rgb(SCARP.fill) },
    { name: 'uRidgeA', type: 'float', value: SCARP.fillAlpha },
    { name: 'uRidgeRimRgb', type: 'vec3', value: rgb(SCARP.rim) },
    { name: 'uRidgeRimA', type: 'float', value: SCARP.rimAlpha },
    { name: 'uRidgeRimW', type: 'float', value: SCARP.rimWidth },
    { name: 'uRidgeShadowRgb', type: 'vec3', value: rgb(SCARP.shadow) },
    { name: 'uRidgeShadowA', type: 'float', value: SCARP.shadowAlpha },
    { name: 'uRidgeShadowOff', type: 'vec2', value: SCARP.shadowOffset },
    { name: 'uScarpFlankOff', type: 'vec2', value: SCARP.flankOffset },
    { name: 'uScarpFlankDrop', type: 'float', value: SCARP.flankAlphaDrop },
    { name: 'uScarpFlankRgb', type: 'vec3[]', value: SCARP.flank.flatMap(rgb), note: '7 entries' },
    { name: 'uScarpCrestRgb', type: 'vec3', value: rgb(SCARP.crest.color) },
    { name: 'uScarpCrestA', type: 'float', value: SCARP.crest.alpha },
    { name: 'uScarpCrestW', type: 'float', value: SCARP.crest.width },
    { name: 'uScarpCrestLift', type: 'float', value: SCARP.crest.lift },
    { name: 'uScarpHairRgb', type: 'vec3', value: rgb(SCARP.hairline.color) },
    { name: 'uScarpHairA', type: 'float', value: SCARP.hairline.alpha },
    { name: 'uScarpHairW', type: 'float', value: SCARP.hairline.width },
    { name: 'uScarpHairLift', type: 'float', value: SCARP.hairline.lift },
  ],
};

const RAMP = F.form.trench;

const FORM_TRENCH: TerrainStylePart & { hachures: boolean } = {
  hachures: RAMP.hachures,
  decl: `
uniform vec3  uFloorRgb;      uniform float uFloorA;
uniform vec3  uTrenchRimRgb;  uniform float uTrenchRimA;  uniform float uTrenchRimW;

uniform vec3  uRampRgb[4];
uniform float uRampA;
uniform float uRampStep;
uniform vec3  uWallRgb;       uniform float uWallA;       uniform float uWallW;
uniform float uWallLift;
`,
  helpers: '',
  helpersWgsl: '',
  paint: `
  // 1 — DEPTH RAMP. The floor, then four bands that fall away to black.
  outc = over(outc, uFloorRgb, uFloorA * covT);

  // Each band starts a further uRampStep of the inradius in, so the last
  // one is the bottom of the trench. The gate is the void core's: a
  // component under TERRAIN_VOID_MIN_CELLS has no inradius to fall over.
  if (kind0 == 1 && compR > 0.0) {
    float dEdt = distAt(distUv(p)).r;
    float dIn = mix(max(sT, 0.0), dEdt, smoothstep(uEdtBlend.x, uEdtBlend.y, dEdt));
    float u = dIn / compR;
    float dupx = px / compR;
    for (int k = 0; k < 4; k++) {
      float a = clamp((u - uRampStep * float(k + 1)) / dupx + 0.5, 0.0, 1.0);
      outc = over(outc, uRampRgb[k], uRampA * a * covT);
    }
  }

  // The lit far wall: the rim copied toward the light, then the dark
  // perimeter over it.
  outc = over(outc, uWallRgb,
              uWallA * lineCov(abs(sT + uWallLift * nT.y), 0.5 * uWallW, px));
  outc = over(outc, uTrenchRimRgb,
              uTrenchRimA * lineCov(abs(sT), 0.5 * uTrenchRimW, px));
`,
  paintWgsl: `
  // 1 — DEPTH RAMP. The floor, then four bands that fall away to black.
  outc = over(outc, U.uFloorRgb, U.uFloorA * covT);

  // Each band starts a further uRampStep of the inradius in, so the last
  // one is the bottom of the trench. The gate is the void core's: a
  // component under TERRAIN_VOID_MIN_CELLS has no inradius to fall over.
  if (kind0 == 1 && compR > 0.0) {
    let dEdt = distAt(distUv(p)).r;
    let dIn = mix(max(sT, 0.0), dEdt, smoothstep(U.uEdtBlend.x, U.uEdtBlend.y, dEdt));
    let u = dIn / compR;
    let dupx = px / compR;
    for (var k: i32 = 0; k < 4; k++) {
      let a = clamp((u - U.uRampStep * f32(k + 1)) / dupx + 0.5, 0.0, 1.0);
      outc = over(outc, U.uRampRgb[k], U.uRampA * a * covT);
    }
  }

  // The lit far wall: the rim copied toward the light, then the dark
  // perimeter over it.
  outc = over(outc, U.uWallRgb,
              U.uWallA * lineCov(abs(sT + U.uWallLift * nT.y), 0.5 * U.uWallW, px));
  outc = over(outc, U.uTrenchRimRgb,
              U.uTrenchRimA * lineCov(abs(sT), 0.5 * U.uTrenchRimW, px));
`,
  uniforms: [
    { name: 'uFloorRgb', type: 'vec3', value: rgb(RAMP.floor) },
    { name: 'uFloorA', type: 'float', value: RAMP.floorAlpha },
    { name: 'uTrenchRimRgb', type: 'vec3', value: rgb(RAMP.rim.color) },
    { name: 'uTrenchRimA', type: 'float', value: RAMP.rim.alpha },
    { name: 'uTrenchRimW', type: 'float', value: RAMP.rim.width },
    { name: 'uRampRgb', type: 'vec3[]', value: RAMP.ramp.flatMap(rgb), note: '4 entries' },
    { name: 'uRampA', type: 'float', value: RAMP.rampAlpha },
    { name: 'uRampStep', type: 'float', value: RAMP.rampStep },
    { name: 'uWallRgb', type: 'vec3', value: rgb(RAMP.wall.color) },
    { name: 'uWallA', type: 'float', value: RAMP.wall.alpha },
    { name: 'uWallW', type: 'float', value: RAMP.wall.width },
    { name: 'uWallLift', type: 'float', value: RAMP.wall.lift },
  ],
};

// ---------------------------------------------------------------------------
// `material` — Boulder Field over Stratified Wall (card 001/Material)
// ---------------------------------------------------------------------------

const BOULDER = F.material.ridge;

/**
 * The chunk grid of Boulder Field.
 *
 * The reference scatters irregular 6-gons inside each traced loop. A
 * fragment shader has no loop to scatter inside, so the chunks stand on a
 * world-space grid of `uChunkPitch` and each one is moved off its own cell
 * centre by a hash. A pixel therefore reads the NINE cells around it and no
 * more, whatever the size of the mass, and the whole field is multiplied by
 * the mass coverage so no chunk pokes out of the rock.
 */
const BOULDER_HELPERS = `
// The six face normals of a regular hexagon, at 30 degrees off each vertex.
const vec2 HEXN[6] = vec2[6](
  vec2( 0.5 * SQ3,  0.5), vec2(0.0,  1.0), vec2(-0.5 * SQ3,  0.5),
  vec2(-0.5 * SQ3, -0.5), vec2(0.0, -1.0), vec2( 0.5 * SQ3, -0.5));

// Premultiplied source-over of one whole colour on another. The chunk
// field is composed on its own before it lands on the ground, so a chunk's
// cast shadow can never darken a chunk the loop reached later.
vec4 overRgba(vec4 dst, vec4 src) { return src + dst * (1.0 - src.a); }

// The signed distance to ONE chunk, positive inside, and the outward
// normal of the face the pixel is nearest to.
//
// q is the pixel in the chunk's own frame, already turned by -rot. The
// hexagon is the intersection of six half-planes at the apothem, so the
// inside distance is the smallest of the six and the face that gives it
// carries the normal. The normal is piecewise constant, which is exactly
// why this treatment needs no screen-space derivative. The y squash is
// applied to q, so the distance is exact across the shape and about
// 1/squash too large along it.
float hexSd(vec2 q, float r, float squash, out vec2 n) {
  q.y /= squash;
  float a = 0.5 * SQ3 * r;
  float best = 1e9;
  vec2 bn = HEXN[0];
  for (int i = 0; i < 6; i++) {
    float d = a - dot(q, HEXN[i]);
    if (d < best) { best = d; bn = HEXN[i]; }
  }
  n = normalize(vec2(bn.x, bn.y * squash));
  return best;
}
`;

/**
 * The WGSL twin of `BOULDER_HELPERS`.
 *
 * Three rules of the port show here. The face table is a module-scope const
 * array, as the hex tables of the prelude are. The `out` parameter of
 * `hexSd` is a pointer the callee writes through. And `q` is a parameter,
 * which WGSL makes immutable, so the squash is applied to a copy.
 */
const BOULDER_HELPERS_WGSL = `
// The six face normals of a regular hexagon, at 30 degrees off each vertex.
const HEXN = array<vec2f, 6>(
  vec2f( 0.5 * SQ3,  0.5), vec2f(0.0,  1.0), vec2f(-0.5 * SQ3,  0.5),
  vec2f(-0.5 * SQ3, -0.5), vec2f(0.0, -1.0), vec2f( 0.5 * SQ3, -0.5));

// Premultiplied source-over of one whole colour on another. The chunk
// field is composed on its own before it lands on the ground, so a chunk's
// cast shadow can never darken a chunk the loop reached later.
fn overRgba(dst: vec4f, src: vec4f) -> vec4f { return src + dst * (1.0 - src.a); }

// The signed distance to ONE chunk, positive inside, and the outward
// normal of the face the pixel is nearest to.
//
// q is the pixel in the chunk's own frame, already turned by -rot. The
// hexagon is the intersection of six half-planes at the apothem, so the
// inside distance is the smallest of the six and the face that gives it
// carries the normal. The normal is piecewise constant, which is exactly
// why this treatment needs no screen-space derivative. The y squash is
// applied to q, so the distance is exact across the shape and about
// 1/squash too large along it.
fn hexSd(qIn: vec2f, r: f32, squash: f32, n: ptr<function, vec2f>) -> f32 {
  var q = qIn;
  q.y /= squash;
  let a = 0.5 * SQ3 * r;
  var best = 1e9;
  var bn = HEXN[0];
  for (var i: i32 = 0; i < 6; i++) {
    let d = a - dot(q, HEXN[i]);
    if (d < best) { best = d; bn = HEXN[i]; }
  }
  (*n) = normalize(vec2f(bn.x, bn.y * squash));
  return best;
}
`;

const MATERIAL_RIDGE: TerrainStylePart = {
  decl: `
uniform vec3  uRidgeRgb;      uniform float uRidgeA;
uniform vec3  uRidgeRimRgb;   uniform float uRidgeRimA;   uniform float uRidgeRimW;

uniform vec3  uRidgeShadowRgb; uniform float uRidgeShadowA; uniform vec2 uRidgeShadowOff;
uniform float uChunkPitch;
uniform float uChunkJitter;
uniform float uChunkRMin;
uniform float uChunkRMax;
uniform float uChunkSquash;
uniform vec3  uChunkRgb;
uniform vec3  uChunkShadowRgb; uniform float uChunkShadowA; uniform vec2 uChunkShadowOff;
uniform vec3  uChunkLitRgb;   uniform float uChunkLitA;   uniform float uChunkLitW;
uniform vec2  uChunkLight;
`,
  helpers: BOULDER_HELPERS,
  helpersWgsl: BOULDER_HELPERS_WGSL,
  paint: `
  // 5 — BOULDER FIELD. The mass's own cast shadow, then the mass, then
  // the rubble over it, then the perimeter.
  outc = over(outc, uRidgeShadowRgb,
              uRidgeShadowA * fillCov(sR - dot(uRidgeShadowOff, nR), px));
  outc = over(outc, uRidgeRgb, uRidgeA * covR);

  if (covR > 0.0) {
    vec2 g0 = floor(p / uChunkPitch);
    float shade = 0.0;
    vec4 rubble = vec4(0.0);
    for (int dy = -1; dy <= 1; dy++) {
      for (int dx = -1; dx <= 1; dx++) {
        vec2 gc = g0 + vec2(float(dx), float(dy));
        uint h = hashU(uint(int(gc.x) + 4096) * 0x9e3779b9u
                       + uint(int(gc.y) + 4096) + uint(uHachureSeed));
        vec2 jit = vec2(hash01(h), hash01(hashU(h + 0x85ebca6bu))) - 0.5;
        float rr = mix(uChunkRMin, uChunkRMax, hash01(hashU(h + 0xc2b2ae35u)));
        float rot = hash01(hashU(h + 0x27d4eb2fu)) * radians(360.0);
        vec2 ctr = (gc + 0.5) * uChunkPitch + jit * (uChunkJitter * uChunkPitch);
        // Into the chunk's own frame, so the six face normals are constant.
        vec2 d = p - ctr;
        float cs = cos(rot), sn = sin(rot);
        vec2 q = vec2(cs * d.x + sn * d.y, -sn * d.x + cs * d.y);
        vec2 nq;
        float sd = hexSd(q, rr, uChunkSquash, nq);
        vec2 nw = vec2(cs * nq.x - sn * nq.y, sn * nq.x + cs * nq.y);
        // The chunk's cast shadow is the chunk itself, translated.
        shade = max(shade, fillCov(sd + dot(uChunkShadowOff, nw), px));
        float a = fillCov(sd, px);
        rubble = overRgba(rubble, vec4(uChunkRgb * a, a));
        // The lit facet: the rim of the faces that look at the light.
        float lit = uChunkLitA * max(0.0, dot(nw, uChunkLight))
                  * lineCov(abs(sd), 0.5 * uChunkLitW, px);
        rubble = overRgba(rubble, vec4(uChunkLitRgb * lit, lit));
      }
    }
    outc = over(outc, uChunkShadowRgb, uChunkShadowA * shade * covR);
    outc = overRgba(outc, rubble * covR);
  }

  outc = over(outc, uRidgeRimRgb,
              uRidgeRimA * lineCov(abs(sR), 0.5 * uRidgeRimW, px));
`,
  paintWgsl: `
  // 5 — BOULDER FIELD. The mass's own cast shadow, then the mass, then
  // the rubble over it, then the perimeter.
  outc = over(outc, U.uRidgeShadowRgb,
              U.uRidgeShadowA * fillCov(sR - dot(U.uRidgeShadowOff, nR), px));
  outc = over(outc, U.uRidgeRgb, U.uRidgeA * covR);

  if (covR > 0.0) {
    let g0 = floor(p / U.uChunkPitch);
    var shade = 0.0;
    var rubble = vec4f(0.0);
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let gc = g0 + vec2f(f32(dx), f32(dy));
        let h = hashU(u32(i32(gc.x) + 4096) * 0x9e3779b9u
                      + u32(i32(gc.y) + 4096) + u32(U.uHachureSeed));
        let jit = vec2f(hash01(h), hash01(hashU(h + 0x85ebca6bu))) - 0.5;
        let rr = mix(U.uChunkRMin, U.uChunkRMax, hash01(hashU(h + 0xc2b2ae35u)));
        let rot = hash01(hashU(h + 0x27d4eb2fu)) * radians(360.0);
        let ctr = (gc + 0.5) * U.uChunkPitch + jit * (U.uChunkJitter * U.uChunkPitch);
        // Into the chunk's own frame, so the six face normals are constant.
        let d = p - ctr;
        let cs = cos(rot);
        let sn = sin(rot);
        let q = vec2f(cs * d.x + sn * d.y, -sn * d.x + cs * d.y);
        var nq = vec2f(0.0);
        let sd = hexSd(q, rr, U.uChunkSquash, &nq);
        let nw = vec2f(cs * nq.x - sn * nq.y, sn * nq.x + cs * nq.y);
        // The chunk's cast shadow is the chunk itself, translated.
        shade = max(shade, fillCov(sd + dot(U.uChunkShadowOff, nw), px));
        let a = fillCov(sd, px);
        rubble = overRgba(rubble, vec4f(U.uChunkRgb * a, a));
        // The lit facet: the rim of the faces that look at the light.
        let lit = U.uChunkLitA * max(0.0, dot(nw, U.uChunkLight))
                * lineCov(abs(sd), 0.5 * U.uChunkLitW, px);
        rubble = overRgba(rubble, vec4f(U.uChunkLitRgb * lit, lit));
      }
    }
    outc = over(outc, U.uChunkShadowRgb, U.uChunkShadowA * shade * covR);
    outc = overRgba(outc, rubble * covR);
  }

  outc = over(outc, U.uRidgeRimRgb,
              U.uRidgeRimA * lineCov(abs(sR), 0.5 * U.uRidgeRimW, px));
`,
  uniforms: [
    { name: 'uRidgeRgb', type: 'vec3', value: rgb(BOULDER.fill) },
    { name: 'uRidgeA', type: 'float', value: BOULDER.fillAlpha },
    { name: 'uRidgeRimRgb', type: 'vec3', value: rgb(BOULDER.rim) },
    { name: 'uRidgeRimA', type: 'float', value: BOULDER.rimAlpha },
    { name: 'uRidgeRimW', type: 'float', value: BOULDER.rimWidth },
    { name: 'uRidgeShadowRgb', type: 'vec3', value: rgb(BOULDER.shadow) },
    { name: 'uRidgeShadowA', type: 'float', value: BOULDER.shadowAlpha },
    { name: 'uRidgeShadowOff', type: 'vec2', value: BOULDER.shadowOffset },
    { name: 'uChunkPitch', type: 'float', value: BOULDER.chunkPitch },
    { name: 'uChunkJitter', type: 'float', value: BOULDER.chunkJitter },
    { name: 'uChunkRMin', type: 'float', value: BOULDER.chunkRMin },
    { name: 'uChunkRMax', type: 'float', value: BOULDER.chunkRMax },
    { name: 'uChunkSquash', type: 'float', value: BOULDER.chunkSquash },
    { name: 'uChunkRgb', type: 'vec3', value: rgb(BOULDER.chunk) },
    { name: 'uChunkShadowRgb', type: 'vec3', value: rgb(BOULDER.chunkShadow.color) },
    { name: 'uChunkShadowA', type: 'float', value: BOULDER.chunkShadow.alpha },
    { name: 'uChunkShadowOff', type: 'vec2', value: BOULDER.chunkShadow.offset },
    { name: 'uChunkLitRgb', type: 'vec3', value: rgb(BOULDER.chunkLit.color) },
    { name: 'uChunkLitA', type: 'float', value: BOULDER.chunkLit.alpha },
    { name: 'uChunkLitW', type: 'float', value: BOULDER.chunkLit.width },
    { name: 'uChunkLight', type: 'vec2', value: BOULDER.chunkLit.light },
  ],
};

const STRATA = F.material.trench;

const MATERIAL_TRENCH: TerrainStylePart & { hachures: boolean } = {
  hachures: STRATA.hachures,
  // `glMod` rides the trench half, because the dash phase of the six bands
  // is the one `mod()` of this file and it takes a negative operand: the
  // tangent coordinate s is a world measure, and a world point behind the
  // origin answers below zero. WGSL has no `mod`, and `%` there keeps the
  // sign of the dividend.
  helpersWgsl: GLMOD_WGSL,
  decl: `
uniform vec3  uFloorRgb;      uniform float uFloorA;
uniform vec3  uTrenchRimRgb;  uniform float uTrenchRimA;  uniform float uTrenchRimW;

uniform vec3  uShelfRgb;      uniform float uShelfA;      uniform float uShelfW;
uniform float uShelfLift;
uniform vec3  uBandRgb;
uniform float uBandA[6];
uniform float uBandStep;
uniform float uBandW;
uniform vec2  uBandOn;
uniform vec2  uBandOff;
uniform vec3  uLitRimRgb;     uniform float uLitRimA;     uniform float uLitRimW;
uniform float uLitRimLift;
`,
  helpers: '',
  paint: `
  // 1 — STRATIFIED WALL. The floor, then the broad lit shelf under the
  // far lip, cut to the floor so it never runs out over the water.
  outc = over(outc, uFloorRgb, uFloorA * covT);
  outc = over(outc, uShelfRgb,
              uShelfA * lineCov(abs(sT + uShelfLift * nT.y), 0.5 * uShelfW, px) * covT);

  // Six sediment bands that FOLLOW the cut: each one is an iso-distance
  // line of the trench, dashed along its own tangent. The dash length and
  // the phase come from a hash of the band index, so the six do not line
  // up. The tangent coordinate is a straight-line measure, so a dash
  // shortens on a tight bend; §4.4 states the limit.
  if (kind0 == 1 && compR > 0.0) {
    float dEdt = distAt(distUv(p)).r;
    float dIn = mix(max(sT, 0.0), dEdt, smoothstep(uEdtBlend.x, uEdtBlend.y, dEdt));
    float u = dIn / compR;
    float s = dot(p, vec2(-nT.y, nT.x));
    for (int k = 0; k < 6; k++) {
      uint h = hashU(uint(k + 1) * 0x9e3779b9u + uint(uHachureSeed));
      float on = mix(uBandOn.x, uBandOn.y, hash01(h));
      float off = mix(uBandOff.x, uBandOff.y, hash01(hashU(h + 0x85ebca6bu)));
      float period = on + off;
      float cyc = mod(s + hash01(hashU(h + 0xc2b2ae35u)) * period, period);
      float d = abs(u - uBandStep * float(k + 1)) * compR;
      outc = over(outc, uBandRgb,
                  uBandA[k] * step(cyc, on) * lineCov(d, 0.5 * uBandW, px) * covT);
    }
  }

  // The lit rim, then the dark perimeter over it.
  outc = over(outc, uLitRimRgb,
              uLitRimA * lineCov(abs(sT + uLitRimLift * nT.y), 0.5 * uLitRimW, px));
  outc = over(outc, uTrenchRimRgb,
              uTrenchRimA * lineCov(abs(sT), 0.5 * uTrenchRimW, px));
`,
  paintWgsl: `
  // 1 — STRATIFIED WALL. The floor, then the broad lit shelf under the
  // far lip, cut to the floor so it never runs out over the water.
  outc = over(outc, U.uFloorRgb, U.uFloorA * covT);
  outc = over(outc, U.uShelfRgb,
              U.uShelfA * lineCov(abs(sT + U.uShelfLift * nT.y), 0.5 * U.uShelfW, px) * covT);

  // Six sediment bands that FOLLOW the cut: each one is an iso-distance
  // line of the trench, dashed along its own tangent. The dash length and
  // the phase come from a hash of the band index, so the six do not line
  // up. The tangent coordinate is a straight-line measure, so a dash
  // shortens on a tight bend; §4.4 states the limit.
  //
  // The six alphas are a float[6], which the uniform address space carries
  // as two vec4f, so the read is [k / 4][k % 4] (wgsl/layout.ts).
  if (kind0 == 1 && compR > 0.0) {
    let dEdt = distAt(distUv(p)).r;
    let dIn = mix(max(sT, 0.0), dEdt, smoothstep(U.uEdtBlend.x, U.uEdtBlend.y, dEdt));
    let u = dIn / compR;
    let s = dot(p, vec2f(-nT.y, nT.x));
    for (var k: i32 = 0; k < 6; k++) {
      let h = hashU(u32(k + 1) * 0x9e3779b9u + u32(U.uHachureSeed));
      let on = mix(U.uBandOn.x, U.uBandOn.y, hash01(h));
      let off = mix(U.uBandOff.x, U.uBandOff.y, hash01(hashU(h + 0x85ebca6bu)));
      let period = on + off;
      let cyc = glMod(s + hash01(hashU(h + 0xc2b2ae35u)) * period, period);
      let d = abs(u - U.uBandStep * f32(k + 1)) * compR;
      outc = over(outc, U.uBandRgb,
                  U.uBandA[k / 4][k % 4] * step(cyc, on)
                    * lineCov(d, 0.5 * U.uBandW, px) * covT);
    }
  }

  // The lit rim, then the dark perimeter over it.
  outc = over(outc, U.uLitRimRgb,
              U.uLitRimA * lineCov(abs(sT + U.uLitRimLift * nT.y), 0.5 * U.uLitRimW, px));
  outc = over(outc, U.uTrenchRimRgb,
              U.uTrenchRimA * lineCov(abs(sT), 0.5 * U.uTrenchRimW, px));
`,
  uniforms: [
    { name: 'uFloorRgb', type: 'vec3', value: rgb(STRATA.floor) },
    { name: 'uFloorA', type: 'float', value: STRATA.floorAlpha },
    { name: 'uTrenchRimRgb', type: 'vec3', value: rgb(STRATA.rim.color) },
    { name: 'uTrenchRimA', type: 'float', value: STRATA.rim.alpha },
    { name: 'uTrenchRimW', type: 'float', value: STRATA.rim.width },
    { name: 'uShelfRgb', type: 'vec3', value: rgb(STRATA.shelf.color) },
    { name: 'uShelfA', type: 'float', value: STRATA.shelf.alpha },
    { name: 'uShelfW', type: 'float', value: STRATA.shelf.width },
    { name: 'uShelfLift', type: 'float', value: STRATA.shelf.lift },
    { name: 'uBandRgb', type: 'vec3', value: rgb(STRATA.band) },
    { name: 'uBandA', type: 'float[]', value: STRATA.bandAlpha, note: '6 entries' },
    { name: 'uBandStep', type: 'float', value: STRATA.bandStep },
    { name: 'uBandW', type: 'float', value: STRATA.bandWidth },
    { name: 'uBandOn', type: 'vec2', value: STRATA.dashOn },
    { name: 'uBandOff', type: 'vec2', value: STRATA.dashOff },
    { name: 'uLitRimRgb', type: 'vec3', value: rgb(STRATA.litRim.color) },
    { name: 'uLitRimA', type: 'float', value: STRATA.litRim.alpha },
    { name: 'uLitRimW', type: 'float', value: STRATA.litRim.width },
    { name: 'uLitRimLift', type: 'float', value: STRATA.litRim.lift },
  ],
};

// ---------------------------------------------------------------------------
// `chart` — Rock as rock (card 05 of round 145, R3276)
// ---------------------------------------------------------------------------

const FLAT = F.chart.ridge;

/**
 * The ridge as a chart draws it: ONE flat fill and ONE hard rim. The
 * contour rings of `default` are dropped, because under this look the rings
 * stand in the water as isobaths (`chart/pieces/isobaths.ts`).
 */
const CHART_RIDGE: TerrainStylePart = {
  decl: `
uniform vec3  uRidgeRgb;      uniform float uRidgeA;
uniform vec3  uRidgeRimRgb;   uniform float uRidgeRimA;   uniform float uRidgeRimW;
`,
  helpers: '',
  helpersWgsl: '',
  paint: `
  // 5 — the flat rock, then its one rim.
  outc = over(outc, uRidgeRgb, uRidgeA * covR);
  outc = over(outc, uRidgeRimRgb,
              uRidgeRimA * lineCov(abs(sR), 0.5 * uRidgeRimW, px));
`,
  paintWgsl: `
  // 5 — the flat rock, then its one rim.
  outc = over(outc, U.uRidgeRgb, U.uRidgeA * covR);
  outc = over(outc, U.uRidgeRimRgb,
              U.uRidgeRimA * lineCov(abs(sR), 0.5 * U.uRidgeRimW, px));
`,
  uniforms: [
    { name: 'uRidgeRgb', type: 'vec3', value: rgb(FLAT.fill) },
    { name: 'uRidgeA', type: 'float', value: FLAT.fillAlpha },
    { name: 'uRidgeRimRgb', type: 'vec3', value: rgb(FLAT.rim) },
    { name: 'uRidgeRimA', type: 'float', value: FLAT.rimAlpha },
    { name: 'uRidgeRimW', type: 'float', value: FLAT.rimWidth },
  ],
};

const HOLE = F.chart.trench;

/**
 * The trench as a deep sounding hole: a darker floor and one clean rim. No
 * hachure and no void core.
 */
const CHART_TRENCH: TerrainStylePart & { hachures: boolean } = {
  hachures: HOLE.hachures,
  decl: `
uniform vec3  uFloorRgb;      uniform float uFloorA;
uniform vec3  uTrenchRimRgb;  uniform float uTrenchRimA;  uniform float uTrenchRimW;
`,
  helpers: '',
  helpersWgsl: '',
  paint: `
  // 1 — the hole: the darker floor, then its one rim.
  outc = over(outc, uFloorRgb, uFloorA * covT);
  outc = over(outc, uTrenchRimRgb,
              uTrenchRimA * lineCov(abs(sT), 0.5 * uTrenchRimW, px));
`,
  paintWgsl: `
  // 1 — the hole: the darker floor, then its one rim.
  outc = over(outc, U.uFloorRgb, U.uFloorA * covT);
  outc = over(outc, U.uTrenchRimRgb,
              U.uTrenchRimA * lineCov(abs(sT), 0.5 * U.uTrenchRimW, px));
`,
  uniforms: [
    { name: 'uFloorRgb', type: 'vec3', value: rgb(HOLE.floor) },
    { name: 'uFloorA', type: 'float', value: HOLE.floorAlpha },
    { name: 'uTrenchRimRgb', type: 'vec3', value: rgb(HOLE.rim.color) },
    { name: 'uTrenchRimA', type: 'float', value: HOLE.rim.alpha },
    { name: 'uTrenchRimW', type: 'float', value: HOLE.rim.width },
  ],
};

// ---------------------------------------------------------------------------
// The roster, and what `terrain.ts` asks of it
// ---------------------------------------------------------------------------

/** Every terrain look, keyed by its `labels.terrain_styles` id. */
export const TERRAIN_STYLE_DEFS: Record<TerrainStyle, TerrainStyleDef> = {
  default: { ridge: DEFAULT_RIDGE, trench: DEFAULT_TRENCH },
  form: { ridge: FORM_RIDGE, trench: FORM_TRENCH },
  material: { ridge: MATERIAL_RIDGE, trench: MATERIAL_TRENCH },
  chart: { ridge: CHART_RIDGE, trench: CHART_TRENCH },
};

/**
 * The look of one id. The id is one of `TERRAIN_STYLES` by type, and the
 * look resolver (`render/scene-look.ts`) holds a theme body to the same
 * list, so no unknown id reaches this table.
 */
function terrainStyleDef(id: TerrainStyle): TerrainStyleDef {
  return TERRAIN_STYLE_DEFS[id];
}

/** Every uniform ONE look owns: the trench half first, then the ridge half. */
export function terrainStyleUniforms(id: TerrainStyle): readonly UniformSpec[] {
  const def = terrainStyleDef(id);
  return [...def.trench.uniforms, ...def.ridge.uniforms];
}

/** The declarations of one look, in the same order as its uniforms. */
export function terrainStyleDecl(id: TerrainStyle): string {
  const def = terrainStyleDef(id);
  return def.trench.decl + def.ridge.decl;
}

/** The helper functions of one look, in front of `terrainPaint`. */
export function terrainStyleHelpers(id: TerrainStyle): string {
  const def = terrainStyleDef(id);
  return def.trench.helpers + def.ridge.helpers;
}

/** The WGSL twin of `terrainStyleHelpers`, in the same order. */
export function terrainStyleHelpersWgsl(id: TerrainStyle): string {
  const def = terrainStyleDef(id);
  return def.trench.helpersWgsl + def.ridge.helpersWgsl;
}

/**
 * `terrainPaint` for one look: the shared head, the trench, the ridge and
 * the water-lining of the chart's card 01, which every look carries.
 *
 * `nT` and `nR` are the unit INWARD normals of the two bodies. `sT` and
 * `sR` are positive inside, so the gradient of each field points inward and
 * the normal is that gradient. A treatment that copies a body toward the
 * light or away from it reads them (the translated-copy rule above).
 */
export function terrainPaintSrc(id: TerrainStyle): string {
  const def = terrainStyleDef(id);
  return `
// ---- the picture, from the two signed distances and the plane ----------
//
// sT and sR are the world-px distances to the trench rim and to the ridge
// perimeter, positive inside. Where they come from is the only difference
// between the two programs. nT and nR are the unit inward normals of the
// same two fields.
vec4 terrainPaint(vec2 p, ivec2 c0, int kind0, float px, float sT, float sR, vec2 nT, vec2 nR) {
  float covT = fillCov(sT, px);
  float covR = fillCov(sR, px);
  float compR = inGrid(c0) ? texelFetch(uCompR, c0, 0).r : 0.0;

  vec4 outc = vec4(0.0);
${def.trench.paint}${def.ridge.paint}
  // 8 — the water-lining of card 01 (R548, §17.3.01), the historical
  // reading alone. Four iso-lines of the SAME sR the rim above is drawn
  // from, on the water outside rock, each one a lineCov ramp in the rim's
  // own colour. sR outside rock is the field's signed distance, so the
  // lines follow the field isoline and not a hex outline — which is what
  // makes them read as engraved water and not as a border.
  if (uWaterLines == 1 && sR < 0.0 && -sR < uLineOut) {
    float outD = -sR;
    for (int i = 0; i < 4; i++) {
      outc = over(outc, uRidgeRimRgb,
                  uLineA[i] * lineCov(abs(outD - uLineD[i]), 0.5 * uLineW[i], px));
    }
  }

  return outc;
}
`;
}

/**
 * The WGSL twin of `terrainPaintSrc`, function for function.
 *
 * Three things change and nothing else. Every uniform is a member of the
 * ONE generated block, so a read is `U.uName`. The component inradius is
 * read with `textureLoad`, and the `inGrid` guard stays an `if`, because
 * WGSL evaluates both arms of a `select`. And a local that is written more
 * than once is a `var`, where a local that is not is a `let`.
 */
export function terrainPaintSrcWgsl(id: TerrainStyle): string {
  const def = terrainStyleDef(id);
  return `
// ---- the picture, from the two signed distances and the plane ----------
//
// sT and sR are the world-px distances to the trench rim and to the ridge
// perimeter, positive inside. Where they come from is the only difference
// between the two programs. nT and nR are the unit inward normals of the
// same two fields.
fn terrainPaint(p: vec2f, c0: vec2i, kind0: i32, px: f32, sT: f32, sR: f32,
                nT: vec2f, nR: vec2f) -> vec4f {
  let covT = fillCov(sT, px);
  let covR = fillCov(sR, px);
  var compR = 0.0;
  if (inGrid(c0)) { compR = textureLoad(uCompR, c0, 0).r; }

  var outc = vec4f(0.0);
${def.trench.paintWgsl}${def.ridge.paintWgsl}
  // 8 — the water-lining of card 01 (R548, §17.3.01), the historical
  // reading alone. Four iso-lines of the SAME sR the rim above is drawn
  // from, on the water outside rock, each one a lineCov ramp in the rim's
  // own colour. sR outside rock is the field's signed distance, so the
  // lines follow the field isoline and not a hex outline — which is what
  // makes them read as engraved water and not as a border.
  if (U.uWaterLines == 1 && sR < 0.0 && -sR < U.uLineOut) {
    let outD = -sR;
    for (var i: i32 = 0; i < 4; i++) {
      outc = over(outc, U.uRidgeRimRgb,
                  U.uLineA[i] * lineCov(abs(outD - U.uLineD[i]), 0.5 * U.uLineW[i], px));
    }
  }

  return outc;
}
`;
}
